import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, Users, Globe, Award, Target, Rocket, Heart, Zap } from "lucide-react";
import companyImage from "@/assets/global-business-network.jpg";
import missionImage from "@/assets/innovation-workspace.jpg";
import { TechAnimation, TeamAnimation } from "@/components/TechAnimations";

const CompanyStory = () => {
  const milestones = [
    {
      year: "2017",
      title: "Foundation & Vision",
      description: "Founded as Smartborn IT Services with focus on custom software development and SAP implementation services.",
      icon: Rocket,
      achievements: ["Bangalore headquarters established", "Initial team of 10 engineers", "First enterprise client engagement"]
    },
    {
      year: "2019",
      title: "Service Expansion",
      description: "Expanded service offerings to include Salesforce, Workday, and cloud migration projects.",
      icon: Globe,
      achievements: ["Team grew to 50+ professionals", "Multiple industry verticals served", "Offshore development center operational"]
    },
    {
      year: "2021",
      title: "Technology Partnerships",
      description: "Established partnerships with major technology vendors and expanded geographic reach.",
      icon: Zap,
      achievements: ["100+ completed projects", "Enterprise client base established", "Technology partner certifications"]
    },
    {
      year: "2023",
      title: "Continued Growth",
      description: "Further expanded capabilities in enterprise applications and AI/ML consulting services.",
      icon: Target,
      achievements: ["Team size 150+", "Expanded industry expertise", "Enhanced service delivery framework"]
    },
    {
      year: "2025",
      title: "Current Operations",
      description: "Operating as full-service IT solutions provider with comprehensive enterprise capabilities.",
      icon: Award,
      achievements: ["200+ team members globally", "Diverse client portfolio", "Multi-domain expertise"]
    }
  ];

  const values = [
    {
      title: "Innovation First",
      description: "We stay ahead of technology trends to deliver cutting-edge solutions that give our clients a competitive advantage.",
      icon: Rocket,
      color: "from-blue-500 to-purple-600"
    },
    {
      title: "Client Success",
      description: "Our success is measured by our clients' success. We're committed to delivering exceptional value and lasting partnerships.",
      icon: Heart,
      color: "from-red-500 to-pink-600"
    },
    {
      title: "Quality Excellence",
      description: "We maintain the highest standards of quality in everything we do, from code to client relationships.",
      icon: Award,
      color: "from-green-500 to-emerald-600"
    },
    {
      title: "Global Collaboration",
      description: "We believe in the power of diverse, global teams working together to solve complex challenges.",
      icon: Globe,
      color: "from-orange-500 to-yellow-600"
    }
  ];

  return (
    <section id="company-story" className="py-12 md:py-16 lg:py-20 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Company Story Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 bg-gradient-primary bg-clip-text text-transparent pb-2">
            Our Journey
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            From a small startup to a global technology partner, discover the milestones that shaped Smartborn's success story.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative max-w-6xl mx-auto">
          {/* Center vertical line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-primary/30 transform -translate-x-1/2 hidden md:block" />
          
          {/* Mobile vertical line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-primary/30 md:hidden" />

          <div className="space-y-8 md:space-y-12">
            {milestones.map((milestone, index) => {
              const Icon = milestone.icon;
              const isLeft = index % 2 === 0;
              
              return (
                <div key={milestone.year} className="relative">
                  {/* Desktop Layout */}
                  <div className="hidden md:grid md:grid-cols-2 md:gap-8 items-center">
                    {isLeft ? (
                      <>
                        {/* Left side content */}
                        <div className="text-right pr-8">
                          <Badge className="bg-primary/10 text-primary border-primary/30 mb-4">
                            {milestone.year}
                          </Badge>
                          <Card className="hover:shadow-elegant transition-all duration-300 bg-card/50 backdrop-blur-sm border-2 border-border/50">
                            <CardContent className="p-6">
                              <h3 className="text-2xl font-bold text-foreground mb-3">
                                {milestone.title}
                              </h3>
                              <p className="text-muted-foreground mb-4">
                                {milestone.description}
                              </p>
                              <ul className="space-y-2 text-left">
                                {milestone.achievements.map((achievement, idx) => (
                                  <li key={idx} className="flex items-start gap-2 text-sm text-foreground/80">
                                    <span className="text-primary mt-1">•</span>
                                    <span>{achievement}</span>
                                  </li>
                                ))}
                              </ul>
                            </CardContent>
                          </Card>
                        </div>
                        
                        {/* Center icon */}
                        <div className="relative flex justify-start">
                          <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center shadow-lg border-4 border-background transform -translate-x-6">
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Center icon */}
                        <div className="relative flex justify-end">
                          <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center shadow-lg border-4 border-background transform translate-x-6">
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                        </div>
                        
                        {/* Right side content */}
                        <div className="text-left pl-8">
                          <Badge className="bg-primary/10 text-primary border-primary/30 mb-4">
                            {milestone.year}
                          </Badge>
                          <Card className="hover:shadow-elegant transition-all duration-300 bg-card/50 backdrop-blur-sm border-2 border-border/50">
                            <CardContent className="p-6">
                              <h3 className="text-2xl font-bold text-foreground mb-3">
                                {milestone.title}
                              </h3>
                              <p className="text-muted-foreground mb-4">
                                {milestone.description}
                              </p>
                              <ul className="space-y-2">
                                {milestone.achievements.map((achievement, idx) => (
                                  <li key={idx} className="flex items-start gap-2 text-sm text-foreground/80">
                                    <span className="text-primary mt-1">•</span>
                                    <span>{achievement}</span>
                                  </li>
                                ))}
                              </ul>
                            </CardContent>
                          </Card>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Mobile Layout */}
                  <div className="md:hidden flex gap-4">
                    {/* Icon */}
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center shadow-lg border-4 border-background">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1">
                      <Badge className="bg-primary/10 text-primary border-primary/30 mb-4">
                        {milestone.year}
                      </Badge>
                      <Card className="hover:shadow-elegant transition-all duration-300 bg-card/50 backdrop-blur-sm border-2 border-border/50">
                        <CardContent className="p-6">
                          <h3 className="text-2xl font-bold text-foreground mb-3">
                            {milestone.title}
                          </h3>
                          <p className="text-muted-foreground mb-4">
                            {milestone.description}
                          </p>
                          <ul className="space-y-2">
                            {milestone.achievements.map((achievement, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-foreground/80">
                                <span className="text-primary mt-1">•</span>
                                <span>{achievement}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Core Values Section */}
        <div className="mt-24">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
              Our Core Values
            </h3>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const ValueIcon = value.icon;
              return (
                <Card 
                  key={index}
                  className="hover:shadow-elegant transition-all duration-300 bg-card/50 backdrop-blur-sm border-2 border-border/50 hover:-translate-y-1"
                >
                  <CardContent className="p-6 text-center">
                    <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${value.color} flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                      <ValueIcon className="w-8 h-8 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-foreground mb-3">
                      {value.title}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompanyStory;